<template>
  <div class="file-upload-manager">
    <!-- Loading Overlay -->
    <div v-if="isSaving" class="upload-loading-overlay">
      <div class="upload-loading-content">
        <b-spinner
          variant="primary"
          style="width: 3rem; height: 3rem"
        ></b-spinner>
        <h5 class="mt-3 mb-2">Đang tải tệp lên...</h5>
        <p class="text-muted">Vui lòng chờ, đang xử lý {{ fileCount }} tệp</p>
      </div>
    </div>

    <!-- Lightbox for Pending Files -->
    <vue-easy-lightbox
      escDisabled
      :visible="previewFile.visible"
      :imgs="previewFile.imgs"
      :index="previewFile.index"
      :class="{ 'pointer-events-none': isSaving }"
      @hide="handleHidePreviewFile"
    ></vue-easy-lightbox>

    <!-- Lightbox for Attachments -->
    <vue-easy-lightbox
      escDisabled
      :visible="previewAttachment.visible"
      :imgs="previewAttachment.imgs"
      :index="previewAttachment.index"
      :class="{ 'pointer-events-none': isSaving }"
      @hide="handleHidePreviewAttachment"
    ></vue-easy-lightbox>

    <!-- Modal preview non-image files -->
    <b-modal
      v-model="filePreviewModal.visible"
      size="xl"
      hide-footer
      body-class="p-0"
    >
      <template #modal-title>
        <div>
          <div>{{ filePreviewModal.fileName }}</div>
          <small
            v-if="filePreviewModal.uploadedByName"
            class="text-muted font-weight-normal"
          >
            Upload bởi: {{ filePreviewModal.uploadedByName }} | 
            Lúc: {{ formatDBToDateSlash({ date: filePreviewModal.createdAt }) }}
          </small>
        </div>
      </template>
      <div v-if="filePreviewModal.category === 'pdf'" style="height: 80vh;">
        <iframe :src="filePreviewModal.url" width="100%" height="100%" style="border: none;" />
      </div>
      <div v-else-if="filePreviewModal.category === 'video'" class="d-flex justify-content-center p-3">
        <video :src="filePreviewModal.url" controls style="max-width: 100%; max-height: 70vh;" />
      </div>
      <div v-else class="text-center p-5">
        <i class="fas fa-file fa-4x text-secondary mb-3 d-block"></i>
        <p class="text-muted">Không thể xem trước loại file này.</p>
        <a :href="filePreviewModal.url" :download="filePreviewModal.fileName" class="btn btn-primary btn-sm">
          <i class="fas fa-download mr-1"></i> Tải về
        </a>
      </div>
    </b-modal>

    <!-- Warning: Chưa có entityId -->
    <b-alert v-if="!entityId" show variant="info" class="mb-3">
      <i class="fas fa-info-circle mr-2"></i>
      <span>Vui lòng <strong>tạo đơn hàng</strong> trước</span>
    </b-alert>

    <!-- Chỉ hiển thị phần quản lý file khi có entityId -->
    <template v-else>
      <!-- Header: Tệp đã đính kèm -->
      <div
        v-if="attachments && attachments.length > 0"
        class="d-flex align-items-center justify-content-between mb-3"
      >
        <span class="font-weight-bold text-dark">
          <i class="fas fa-folder-open text-primary mr-2"></i>
          Tệp đã đính kèm
        </span>
        <div>
          <b-badge
            :variant="
              attachments.length >= FILE_LIMIT.MAX_ATTACHMENTS
                ? 'danger'
                : 'primary'
            "
            pill
            class="mr-1"
          >
            {{ attachments.length }}/{{ FILE_LIMIT.MAX_ATTACHMENTS }} tệp
          </b-badge>
        </div>
      </div>

      <!-- Preview Attachments (Server) -->
      <b-col v-if="isLoadAttachmentSuccess" class="preview-img-container mb-4">
        <div>
          <div class="d-flex flex-wrap">
            <div
              v-for="(item, index) in attachments"
              :key="index"
              v-b-tooltip.hover.html.top="buildAttachmentTooltip(item)"
              class="img-container align-items-start"
            >
              <img
                v-if="typecheck(item.fileName)"
                :src="item.url"
                class="preview-img"
                width="80"
                height="100"
                :alt="item.originalName"
                @click="showMultiplePreViewAttachment(index)"
              />
              <div
                v-else
                class="file-type-icon"
                @click="previewNonImageFile(item)"
              >
                <i :class="getFileTypeIcon(item.originalName || item.fileName)"></i>
              </div>
              <div class="file-name-label">
                {{ truncateFileName(item.originalName || item.fileName) }}
              </div>
              <i
                v-if="!disabled"
                class="fas fa-times-circle text-danger close"
                @click="deleteAttachment(item.id)"
              ></i>
              <a :download="item.originalName || item.fileName" :href="item.url">
                <i class="fas fa-arrow-circle-down text-warning remove"></i>
              </a>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Upload Form -->
      <form
        enctype="multipart/form-data"
        novalidate
        class="mb-4"
        @submit.prevent
      >
        <b-button
          size="sm"
          variant="secondary"
          @click="openFileUpload()"
          class="font-weight-bolder mr-2"
          :disabled="isFailed || disabled || isMaxFilesReached"
        >
          <i class="fas fa-paperclip"></i>
          Đính kèm tệp
        </b-button>

        <!-- Nút upload chỉ hiện khi có pendingFiles -->
        <b-button
          v-if="pendingFiles.length"
          size="sm"
          variant="warning"
          @click="submitFileUpload()"
          class="font-weight-bolder"
          :disabled="isFailed || isSaving"
        >
          <span v-if="isSaving">
            <b-spinner small class="mr-1"></b-spinner>
            Đang tải lên...
          </span>
          <span v-else>
            <i class="fas fa-upload"></i>
            Tải tệp lên
          </span>
        </b-button>

        <b-form-file
          v-model="fileAttach.files"
          ref="upload-files"
          id="upload-files"
          class="mb-2 d-none input-file"
          multiple
          :disabled="isSaving"
          @change="
            filesChange($event.target.files);
            fileCount = $event.target.files.length;
          "
        />

        <p v-if="isSaving">Đang tải {{ fileCount }} tệp...</p>

        <div v-if="isFailed" class="d-flex">
          <small class="text-danger">Tải tệp thất bại.</small>
          <small>
            <a href="javascript:void(0)" @click="reset()">Thử lại</a>
          </small>
          <pre>{{ uploadError }}</pre>
        </div>
      </form>

      <!-- Header: Tệp chờ tải lên -->
      <div v-if="isSuccess && pendingFiles.length > 0">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <span class="font-weight-bold text-dark">
            <i class="fas fa-cloud-upload-alt text-warning mr-2"></i>
            Tệp chờ tải lên đính kèm
          </span>
          <div>
            <b-badge
              :variant="
                pendingFiles.length >= FILE_LIMIT.MAX_PENDING_FILES
                  ? 'danger'
                  : 'warning'
              "
              pill
              class="mr-1"
            >
              {{ pendingFiles.length }}/{{ FILE_LIMIT.MAX_PENDING_FILES }} tệp
            </b-badge>
          </div>
        </div>

        <!-- Preview Pending Files -->
        <b-col class="preview-img-container mb-4">
          <div>
            <div class="d-flex flex-wrap">
              <div
                v-for="(item, index) in pendingFiles"
                :key="index"
                v-b-tooltip.hover.top="item.originalName || item.fileName"
                class="img-container align-items-start"
              >
                <img
                  v-if="typecheck(item.fileName)"
                  :src="item.url"
                  class="preview-img"
                  width="80"
                  height="100"
                  :alt="item.originalName"
                  @click="showMultiplePreViewFile(index)"
                />
                <div
                  v-else
                  class="file-type-icon"
                  @click="previewNonImageFile(item)"
                >
                  <i :class="getFileTypeIcon(item.originalName || item.fileName)"></i>
                </div>
                <div class="file-name-label">
                  {{ truncateFileName(item.originalName || item.fileName) }}
                </div>
                <i
                  class="fas fa-times-circle text-danger close"
                  @click="removeFile(index)"
                ></i>
              </div>
            </div>
          </div>
        </b-col>
      </div>
    </template>
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import JwtService from '@/core/services/jwt.service';
import VueEasyLightbox from 'vue-easy-lightbox';
import { cmdUrl } from '@/utils/apiUrl';
import { makeToastSuccess, makeToastFaile, handleError } from '@/utils/common';
import axios from 'axios';
import { FILE_UPLOAD_LIMITS } from '@/utils/constants';
import { featureFlagMixin } from '@/view/mixins';
import { typecheck, getFileCategory } from '@/utils/common-upload-files';
import { FILE_UPLOAD_MODE } from '@/utils/enum';
import { resizeImages } from '@/utils/resizeImage';
import { formatDBToDateSlash } from '@/utils/date';


export default {
  name: 'FileUploadManager',
  mixins: [featureFlagMixin],
  components: {
    VueEasyLightbox,
  },
  props: {
    entityId: {
      type: [String, Number],
      required: false,
      default: null,
    },
    entity: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      FILE_LIMIT: FILE_UPLOAD_LIMITS,
      attachments: [],
      isLoadAttachmentSuccess: false,
      pendingFiles: [],
      fileAttach: {
        files: [],
      },
      currentStatus: null,
      uploadError: null,
      fileCount: 0,
      previewFile: {
        visible: false,
        imgs: '',
        index: 0,
      },
      previewAttachment: {
        visible: false,
        imgs: '',
        index: 0,
      },
      fileIdCounter: 0,
      filePreviewModal: {
        visible: false,
        url: '',
        fileName: '',
        category: '',
        uploadedByName: '',
        createdAt: '',
      },
    };
  },
  computed: {
    isInitial() {
      return this.currentStatus === FILE_UPLOAD_MODE.INITIAL;
    },
    isSaving() {
      return this.currentStatus === FILE_UPLOAD_MODE.SAVING;
    },
    isSuccess() {
      return this.currentStatus === FILE_UPLOAD_MODE.SUCCESS;
    },
    isFailed() {
      return this.currentStatus === FILE_UPLOAD_MODE.FAILED;
    },
    isEnabledUploadResizeImage() {
      return this.isFeatureActive('upload-resize-image');
    },
    isMaxFilesReached() {
      const totalFiles =
        (this.attachments?.length || 0) + this.pendingFiles.length;
      return totalFiles >= this.FILE_LIMIT.MAX_ATTACHMENTS;
    },
  },
  mounted() {
    this.reset();
    if (this.entityId) {
      this.loadAttachments();
    }
  },
  watch: {
    entityId: {
      handler(newVal) {
        if (newVal) {
          this.loadAttachments();
        }
      },
    },
  },
  methods: {
    typecheck,
    formatDBToDateSlash,
    buildAttachmentTooltip(item) {
      const name = item.originalName || item.fileName;
      if (!item.uploadedByName) return name;
      return `<div>${name}</div><small>Upload bởi: ${item.uploadedByName}</small>`;
    },

    previewNonImageFile(item) {
      const fileName = item.originalName || item.fileName;
      this.filePreviewModal.url = item.blobUrl || item.url;
      this.filePreviewModal.fileName = fileName;
      this.filePreviewModal.uploadedByName = item.uploadedByName || '';
      this.filePreviewModal.createdAt = item.createdAt || '';
      this.filePreviewModal.category = getFileCategory(fileName);
      this.filePreviewModal.visible = true;
    },

    getFileTypeIcon(fileName) {
      const ext = (fileName || '').split('.').pop().toLowerCase();
      if (['pdf'].includes(ext)) return 'fas fa-file-pdf fa-3x text-danger';
      if (['doc', 'docx'].includes(ext)) return 'fas fa-file-word fa-3x text-primary';
      if (['xls', 'xlsx'].includes(ext)) return 'fas fa-file-excel fa-3x text-success';
      if (['mp4', 'mov', 'avi'].includes(ext)) return 'fas fa-file-video fa-3x text-info';
      if (['txt'].includes(ext)) return 'fas fa-file-alt fa-3x text-secondary';
      return 'fas fa-file fa-3x text-secondary';
    },

    truncateFileName(name, maxLength = 12) {
      if (!name) return '';
      if (name.length <= maxLength) return name;
      const ext = name.includes('.') ? '.' + name.split('.').pop() : '';
      return name.substring(0, maxLength - ext.length - 1) + '…' + ext;
    },

    openFileUpload() {
      document.getElementById('upload-files').click();
    },

    async filesChange(fileList) {
      if (!fileList.length) return;

      // Tính tổng file THỰC TẾ (attachments đã upload + pending hiện tại + file mới)
      const totalFiles =
        (this.attachments?.length || 0) +
        this.pendingFiles.length +
        fileList.length;

      if (totalFiles > this.FILE_LIMIT.MAX_ATTACHMENTS) {
        makeToastFaile(
          `Không thể thêm ${fileList.length} tệp. Tổng số tệp (${totalFiles}) vượt quá giới hạn ${this.FILE_LIMIT.MAX_ATTACHMENTS} tệp.`,
        );
        this.resetFileInput();
        return;
      }

      this.currentStatus = FILE_UPLOAD_MODE.SAVING;

      try {
        // Xử lý từng file và thêm vào pendingFiles
        const resizedList = await resizeImages(fileList);
        const newPendingFiles = await Promise.all(
          resizedList.map(async (file, index) => {
            const preview = await this.getImagePreview(file);
            return {
              id: ++this.fileIdCounter,
              file: file,
              originalName: fileList[index].name,
              fileName: fileList[index].name,
              url: preview,
              blobUrl: URL.createObjectURL(file),
            };
          }),
        );

        // Thêm vào danh sách pending
        this.pendingFiles = this.pendingFiles.concat(newPendingFiles);
        this.currentStatus = FILE_UPLOAD_MODE.SUCCESS;
      } catch (err) {
        this.uploadError = err;
        this.currentStatus = FILE_UPLOAD_MODE.FAILED;
      }

      // Reset input để có thể chọn lại
      this.resetFileInput();
    },

    resetFileInput() {
      if (this.$refs['upload-files']) {
        this.$refs['upload-files'].reset();
      }
    },

    getImagePreview(file) {
      return new Promise((resolve, reject) => {
        const fReader = new FileReader();
        fReader.onload = () => {
          resolve(fReader.result);
        };
        fReader.onerror = reject;
        fReader.readAsDataURL(file);
      });
    },

    submitFileUpload() {
      if (!this.entityId) {
        makeToastFaile('Vui lòng lưu đơn hàng trước khi tải tệp lên đính kèm');
        return;
      }

      // Kiểm tra lại tổng file trước khi upload
      const totalFiles =
        (this.attachments?.length || 0) + this.pendingFiles.length;

      if (totalFiles > this.FILE_LIMIT.MAX_ATTACHMENTS) {
        makeToastFaile(
          `Không thể tải lên. Tổng số tệp (${totalFiles}) vượt quá giới hạn ${this.FILE_LIMIT.MAX_ATTACHMENTS} tệp.`,
        );
        return;
      }

      const formData = new FormData();

      // Thêm TẤT CẢ file trong pendingFiles vào formData
      this.pendingFiles.forEach((pendingFile) => {
        formData.append('fileUploads', pendingFile.file);
      });

      this.currentStatus = FILE_UPLOAD_MODE.SAVING;
      const endPoint = `${
        this.isEnabledUploadResizeImage
          ? cmdUrl.File.uploadResizeImage
          : cmdUrl.File.upload
      }?entityId=${this.entityId}&entity=${this.entity}`;

      axios
        .post(endPoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${JwtService.getToken()}`,
          },
        })
        .then(async (resp) => {
          if (resp.status === 200) {
            this.currentStatus = FILE_UPLOAD_MODE.SUCCESS;
            makeToastSuccess(
              `Tải lên thành công ${this.pendingFiles.length} tệp`,
            );
            await this.loadAttachments();
            this.reset(); // Clear pending files sau khi upload thành công
          }
        })
        .catch((error) => {
          handleError(error);
          this.currentStatus = FILE_UPLOAD_MODE.FAILED;
        });
    },

    loadAttachments() {
      if (!this.entityId) {
        return Promise.resolve();
      }

      this.isLoadAttachmentSuccess = false;
      return ApiService.query('file', {
        params: {
          entity: this.entity,
          entityId: this.entityId,
        },
      })
        .then(async (resp) => {
          if (resp.status === 200) {
            const attachments = resp.data.data.map((item) => ({
              id: item.id,
              fileName: item.fileName,
              originalName: item.originalName || item.fileName,
              uploadedByName: item.uploadedByName || null,
              createdAt: item.createdAt || null,
            }));

            this.attachments = await Promise.all(
              attachments.map(async ({ fileName, ...rest }) => {
                const resp = await axios.get(
                  `${cmdUrl.File.download}?filename=${fileName}`,
                  {
                    responseType: 'arraybuffer',
                    headers: {
                      Authorization: `Bearer ${JwtService.getToken()}`,
                    },
                  },
                );
                const contentType = resp.headers['content-type'];
                const blob = new Blob([resp.data], { type: contentType });
                const base64 = await this.blobToBase64(blob);
                return {
                  ...rest,
                  url: base64,
                  blobUrl: URL.createObjectURL(blob),
                  fileName,
                };
              }),
            );
            this.isLoadAttachmentSuccess = true;
          }
        })
        .catch(() => {
          this.isLoadAttachmentSuccess = false;
        });
    },

    blobToBase64(blob) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    },

    deleteAttachment(id) {
      ApiService.delete(`${cmdUrl.File.root}/${id}`).then((deleteResponse) => {
        if (deleteResponse.status === 200) {
          if (deleteResponse.data.data === true) {
            const index = this.attachments.findIndex((item) => item.id === id);
            this.attachments.splice(index, 1);
          }
        }
      });
    },

    removeFile(index) {
      this.pendingFiles.splice(index, 1);

      // Reset status nếu không còn file
      if (this.pendingFiles.length === 0) {
        this.currentStatus = FILE_UPLOAD_MODE.INITIAL;
      }
    },

    handleHidePreviewFile() {
      this.previewFile.visible = false;
    },

    showMultiplePreViewFile(indexOfFile) {
      const files = this.pendingFiles.map((file) => {
        return {
          src: file.url,
          title: file.fileName,
        };
      });
      this.previewFile.imgs = files;
      this.previewFile.index = indexOfFile;
      this.previewFile.visible = true;
    },

    handleHidePreviewAttachment() {
      this.previewAttachment.visible = false;
    },

    showMultiplePreViewAttachment(indexOfFile) {
      const files = this.attachments.map((file) => {
        return {
          src: file.url,
          title: file.fileName,
        };
      });
      this.previewAttachment.imgs = files;
      this.previewAttachment.index = indexOfFile;
      this.previewAttachment.visible = true;
    },

    reset() {
      this.currentStatus = FILE_UPLOAD_MODE.INITIAL;
      this.pendingFiles = [];
      this.uploadError = null;
      this.resetFileInput();
    },
  },
};
</script>

<style lang="scss" scoped>
.file-upload-manager {
  .upload-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .upload-loading-content {
    text-align: center;
  }

  .pointer-events-none {
    pointer-events: none;
    opacity: 0.5;
    cursor: not-allowed;
  }

  .img-container {
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    width: 80px;
    margin: 0px 10px;
  }

  .preview-img {
    max-width: 80px;
    padding: 10px;
    object-fit: contain;
    border: 1px solid #eeecff;
    border-radius: 5px;
    margin-bottom: 4px;
    cursor: pointer;
  }

  .file-type-icon {
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #eeecff;
    border-radius: 5px;
    margin-bottom: 4px;
    cursor: pointer;
    background: #f9f9ff;
  }

  .file-name-label {
    font-size: 10px;
    color: #555;
    text-align: center;
    word-break: break-all;
    max-width: 80px;
    line-height: 1.2;
    margin-bottom: 16px;
  }

  .preview-img-container {
    border: 2px dashed #eeecff;
    padding: 2rem 1rem;
  }

  .close {
    position: absolute;
    top: 0px;
    right: 0px;
    width: 15px;
    cursor: pointer;
  }

  .remove {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 15px;
  }
}
</style>
